// @flow
import React, {Component, Fragment} from 'react';
import {Helmet} from 'react-helmet-async';
import {compose} from 'redux';
import {connect} from 'react-redux';

import gql from 'graphql-tag';
import {Waypoint} from '@homeaway/react-unveil';
import Experiment from '@homeaway/react-experiment';
import {logExperiment} from '@homeaway/abacus-client-utils';

// HA_FAMILY_COLLECTION_BADGE_SERP_AD
import FamilyCollectionBanner, {
    shouldDisplayAtIndex as shouldDisplayFamilyCollectionBannerAtIndex
} from '../FamilyCollectionBanner/FamilyCollectionBanner';

import DatesPromptBanner from '../DatesPromptBanner/DatesPromptBanner';
import Hit, {fragments as hitFragments, hitDataErrors} from '../Hit/Hit';

import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import PinnedProperty from '../PinnedProperty/PinnedProperty';
import {trigger as edapTrigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import ScrollListener from '../ScrollListener/ScrollListener';
import {isDateless} from '../../utils/searchParamsUtils';
import logger from '../../utils/clientLogger';
import {isDifferent} from '../../utils';
import Timing from '../Timing/Timing';
import {media} from '../../utils/breakpoints';
import {mergeListingsCollectionWithPinnedListing} from '../../utils/mergeListingsCollectionWithPinnedListing';
import {reportError} from '@homeaway/react-error-reporter';

import type {ListingShape} from '../Hit/Hit';
import withPAR from '../Timing/withPAR';
import withLingua from '../withLingua/withLingua';
import type {LinguaProps} from '../withLingua/withLingua';
import {triggerListHitClickEDAPEvent} from '../../utils/hitEdapUtils';
import {triggerSearchResultSetPresentedEdapEvent} from '../../utils/collectionEdapUtils';
import {
    HA_FAMILY_COLLECTION_BADGE_SERP_AD,
    HA_PULSE_SAVE_CONFIRMATION_SERP_HIT,
    HA_MULTIUNIT_DECORATOR_TEST
} from '../../abacus/experiments';

import type {Breakpoint} from '../../utils/breakpoints';
import type {State as ReduxState} from '../../reducers';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {TripBoardConfirmationsState} from '../../reducers/tripBoardButton';

import './HitCollection.less';
import {
    hitThumbnailBackgroundImageClass,
    MAX_PRELOAD_HIT_THUMBNAIL
} from './hitCollectionUtils';
import {listingHasPrice} from '../Price/Price';

import {PETS_ALLOWED} from '../../constants/badges';

import {
    MultiUnitHit,
    triggerMultiUnitHitExposedEDAPEvent
} from '@homeaway/react-multi-units';

export const MOBILE_DATES_PROMPT_BANNER_INDEX = 7;

const commonSelectionSet = `
    page
    pageSize
    listings {
        ...HitListing
    }
    pinnedListing {
        listing {
            availableForDates
            ...HitListing
        }
    }
`;
const commonFragments = gql`
    ${hitFragments.listing}
`;

export const fragments = {
    results: gql`
        fragment HitCollectionResults on Results {
            ${commonSelectionSet}
        }
        ${commonFragments}
    `,
    searchResult: gql`
        fragment HitCollectionSearchResult on SearchResult {
            ${commonSelectionSet}
        }
        ${commonFragments}
    `
};

export type PinnedListing = {
    listing: ListingShape
};

type StateProps = {
    breakpoint: Breakpoint,
    currency?: string,
    directEntry: boolean,
    fetchImages?: string,
    fullLocale: string,
    guestsRequested: number,
    inputParams: SearchParamsType,
    isDatelessSearch: boolean,
    isDesktop: boolean,
    isGoodBot: boolean,
    isMetric?: boolean,
    mock?: string,
    multiUnitDecoratorTestIsOn: boolean,
    shortLocale: string,
    numFilters?: number,
    petsRequested: boolean,
    pinnedPropertyLabel?: string,
    searchParamsFilter?: $PropertyType<SearchParamsType, 'filter'>,
    /** @abacus `ha_pulse_save_confirmation_serp_hit` */
    tripBoardConfirmations: TripBoardConfirmationsState,
    tripBoardConfirmationTestIsOn: boolean
};

type OwnProps = {
    results: {
        fromRecord?: number,
        page?: number,
        pageSize?: number,
        listings: ListingShape[],
        pinnedListing: ?PinnedListing
    },
    onScrollHitEnter: (number) => void,
    onHitMouseEnter: (string) => void,
    onHitMouseLeave: () => void
};

export type Props = OwnProps & StateProps & LinguaProps;

export const checkPriceless = (listings: ListingShape[]) => {
    const numPriceless = listings.reduce(
        (acc, l) => acc + (listingHasPrice(l) ? 0 : 1),
        0
    );
    const pricelesslabel: string = numPriceless.toString();
    if (numPriceless > 0) {
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'no price presented',
            highfrequency: 'true',
            eventlabel: pricelesslabel
        });
    }

    const allPriceless = listings.reduce(
        (acc, l) => acc && !listingHasPrice(l),
        listings.length > 0
    );

    if (allPriceless) {
        reportError({name: 'priceless SERP'});
    }
};

export const checkDegradedHitsData = (
    listings: ListingShape[],
    isDesktop: boolean
) => {
    const {degradedHitsCount, missingData} = listings.reduce(
        (logData, listing) => {
            const dataErrors = hitDataErrors(listing, isDesktop);

            if (dataErrors.length) {
                logData.degradedHitsCount = ++logData.degradedHitsCount;

                dataErrors.forEach((error) => {
                    logData.missingData[error] =
                        (logData.missingData[error] || 0) + 1;
                });
            }
            return logData;
        },
        {
            degradedHitsCount: 0,
            missingData: {}
        }
    );

    if (degradedHitsCount > 0) {
        reportError({
            name: 'hits degraded',
            logData: {
                hitsCount: listings.length,
                degradedHitsCount,
                missingData,
                isDesktop
            }
        });
    }
};

export const checkListings = (listings: ListingShape[], isDesktop: boolean) => {
    checkPriceless(listings);
    checkDegradedHitsData(listings, isDesktop);
};

const flatten = (ary) => [].concat(...ary);

export class HitCollection extends Component<Props> {
    componentDidMount() {
        const {
            isDesktop,
            results: {listings},
            inputParams
        } = this.props;

        if (listings && listings.length > 0) {
            checkListings(listings, isDesktop);
            triggerSearchResultSetPresentedEdapEvent(inputParams);
        }
    }

    componentDidUpdate(prevProps: Props) {
        const {
            results: {listings: prevListings}
        } = prevProps;
        const {
            isDesktop,
            results: {listings},
            inputParams
        } = this.props;

        if (prevListings && listings && isDifferent(listings, prevListings)) {
            checkListings(listings, isDesktop);
            triggerSearchResultSetPresentedEdapEvent(inputParams);
        }
    }

    render() {
        const {
            onHitMouseEnter,
            onHitMouseLeave,
            onScrollHitEnter,
            pinnedPropertyLabel,
            results: {
                page,
                pageSize,
                listings: listingsCollection,
                pinnedListing
            },
            breakpoint,
            guestsRequested,
            petsRequested,
            inputParams,
            isDatelessSearch,
            currency,
            isDesktop,
            isMetric,
            messages,
            multiUnitDecoratorTestIsOn,
            shortLocale,
            isGoodBot,
            searchParamsFilter,
            tripBoardConfirmations,
            tripBoardConfirmationTestIsOn
        } = this.props;

        // HA_FAMILY_COLLECTION_BADGE_SERP_AD
        const familyCollectionBannerIndex = shouldDisplayFamilyCollectionBannerAtIndex(
            listingsCollection,
            searchParamsFilter || []
        );

        const listings = mergeListingsCollectionWithPinnedListing(
            listingsCollection,
            pinnedListing
        );

        const pinnedListingId = pinnedListing
            ? pinnedListing.listing.listingId
            : '';

        const handleEnter = (label: number) => {
            onScrollHitEnter(label);
        };

        const handleScroll = () => handleEnter(0);
        return (
            <Timing measureName="hitCollection">
                <ScrollListener
                    useWindowAsScrollParent
                    onScroll={handleScroll}
                    data-wdio="ScrollListener"
                >
                    <div
                        className="HitCollection"
                        data-wdio="HitCollection"
                        role="list"
                    >
                        {flatten(
                            listings.map((listing, index) => {
                                const key = listing.listingId;
                                const hitProps = {
                                    key,
                                    onHitClick: triggerListHitClickEDAPEvent,
                                    listing,
                                    hitIndex: index,
                                    page,
                                    pageSize,
                                    inputParams,
                                    isGoodBot /** @abacus `ha_pulse_save_confirmation_serp_hit` */,
                                    tripBoardConfirmationTestIsOn,
                                    tripBoardConfirmationIsExpanded: !!tripBoardConfirmations[
                                        listing.listingId
                                    ]
                                };

                                let hit = (
                                    <Hit
                                        {...hitProps}
                                        onMouseEnter={onHitMouseEnter}
                                        onMouseLeave={onHitMouseLeave}
                                    />
                                );

                                /**
                                 * BEGIN HIT WRAPPING
                                 */

                                if (listing.listingId === pinnedListingId) {
                                    // SRS sets availability on dates only,
                                    // this way we can construct a more refined message
                                    const guestCountExceeded =
                                        guestsRequested > listing.sleeps;

                                    const amenitiesBadges =
                                        listing.amenitiesBadges || [];

                                    let petsNotAllowed =
                                        petsRequested &&
                                        !amenitiesBadges.find(
                                            (e) => e.id === PETS_ALLOWED
                                        );

                                    const datesUnavailable =
                                        // graphql-traveler-property returns `null` for this field when it can't compute it (for dateless search there is no availability calendar returned
                                        // Since search-service returned this property, assuming its available
                                        // search-results-service is making the same assumption
                                        listing.availableForDates === false;

                                    hit = (
                                        <PinnedProperty
                                            datesUnavailable={datesUnavailable}
                                            guestCountExceeded={
                                                guestCountExceeded
                                            }
                                            key={key}
                                            label={pinnedPropertyLabel}
                                            numResults={listings.length}
                                            petsNotAllowed={petsNotAllowed}
                                        >
                                            {hit}
                                        </PinnedProperty>
                                    );
                                }

                                // Adds waypoint instrumentation so we can fire
                                // callbacks when scrolling past hits 5 & 10 and
                                // send analytics events. These are legacy events.
                                // Can we stop firing them?
                                if ([4, 9].includes(index)) {
                                    hit = (
                                        <div
                                            data-wdio={`Waypoint${index + 1}`}
                                            key={key}
                                        >
                                            <Waypoint
                                                label={index + 1}
                                                onEnter={handleEnter}
                                                useWindowAsScrollParent
                                            >
                                                {hit}
                                            </Waypoint>
                                        </div>
                                    );
                                }

                                if (
                                    listing.multiUnitProperty &&
                                    listing.listingId !== pinnedListingId
                                ) {
                                    const multiUnitProps = {
                                        listing,
                                        onMouseEnter: onHitMouseEnter,
                                        onMouseLeave: onHitMouseLeave,
                                        inputParams,
                                        isDesktop,
                                        currency,
                                        isMetric,
                                        isDatelessSearch,
                                        clientLogger: logger,
                                        lingua: {
                                            key: shortLocale,
                                            locale: shortLocale,
                                            messages
                                        }
                                    };
                                    const handleMultiUnitHitExposed = () => {
                                        logExperiment(
                                            HA_MULTIUNIT_DECORATOR_TEST
                                        );
                                        triggerMultiUnitHitExposedEDAPEvent({
                                            hitIndex: index,
                                            listing,
                                            page,
                                            pageSize
                                        });
                                    };

                                    hit = (
                                        <div
                                            data-wdio={`Waypoint${index + 1}`}
                                            key={key}
                                        >
                                            <Waypoint
                                                label={index + 1}
                                                onEnter={
                                                    handleMultiUnitHitExposed
                                                }
                                                useWindowAsScrollParent
                                            >
                                                {multiUnitDecoratorTestIsOn ? (
                                                    /* istanbul ignore next */
                                                    <ErrorBoundary>
                                                        <MultiUnitHit
                                                            {...multiUnitProps}
                                                        >
                                                            {hit}
                                                        </MultiUnitHit>
                                                    </ErrorBoundary>
                                                ) : (
                                                    <React.Fragment>
                                                        {hit}
                                                    </React.Fragment>
                                                )}
                                            </Waypoint>
                                        </div>
                                    );
                                }

                                /**
                                 * END HIT WRAPPING.
                                 */

                                /**
                                 * Now, return either the hit or an array with
                                 * the hit + an inserted item like a banner.
                                 * Don't forget to add a key for inserted items!
                                 */
                                if (
                                    index ===
                                        MOBILE_DATES_PROMPT_BANNER_INDEX &&
                                    isDatelessSearch &&
                                    !media(breakpoint, {
                                        minWidth: 'SM_MIN'
                                    })
                                ) {
                                    return [
                                        hit,
                                        <DatesPromptBanner
                                            key={'dates-prompt-banner'}
                                            useXsImage
                                        />
                                    ];
                                }

                                // Will be -1 if family collection banner isn't
                                // eligible to be displayed. Mobile Dates Prompt
                                // banner takes precedence.
                                if (index === familyCollectionBannerIndex)
                                    return [
                                        hit,
                                        <Experiment
                                            name={
                                                HA_FAMILY_COLLECTION_BADGE_SERP_AD
                                            }
                                            key={
                                                HA_FAMILY_COLLECTION_BADGE_SERP_AD
                                            }
                                            variant={'on'}
                                        >
                                            <FamilyCollectionBanner />
                                        </Experiment>
                                    ];

                                /**
                                 * If no inserted items, just return the hit
                                 */
                                return hit;
                            })
                        )}
                    </div>
                </ScrollListener>
            </Timing>
        );
    }
}

const mapStateToProps = ({
    abacus,
    viewport: {breakpoint},
    system: {
        currency,
        directEntry,
        fullLocale,
        isDesktop,
        isGoodBot,
        isMetric,
        locale
    },
    searchParams: {params, numFilters, inputParams},
    pinnedProperty: {label},
    router: {query},
    tripboardButton: {confirmations = {}} = {}
}: ReduxState): StateProps => {
    const {adultsCount = 0, childrenCount = 0, petIncluded = false} = params;
    const {mock} = query;

    return {
        breakpoint,
        currency,
        directEntry,
        fetchImages: params.fetchImages,
        fullLocale,
        guestsRequested: adultsCount + childrenCount,
        inputParams,
        isDatelessSearch: isDateless(params),
        isDesktop,
        isGoodBot,
        isMetric,
        shortLocale: locale,
        mock,
        multiUnitDecoratorTestIsOn:
            (abacus[HA_MULTIUNIT_DECORATOR_TEST] || {}).value > 0 || false,
        numFilters,
        petsRequested: petIncluded,
        pinnedPropertyLabel: label,
        searchParamsFilter: params.filter,
        /** @abacus `ha_pulse_save_confirmation_serp_hit` */
        tripBoardConfirmations: confirmations,
        tripBoardConfirmationTestIsOn:
            (abacus[HA_PULSE_SAVE_CONFIRMATION_SERP_HIT] || {}).value > 0 ||
            false
    };
};

export const ConnectedHitCollection = compose(
    connect(mapStateToProps),
    withPAR,
    withLingua({
        namespace: 'traveler/search',
        category: 'messages'
    })
)(HitCollection);

const HitCollectionWithHelmet = (props: OwnProps) => {
    const {listings: listingsCollection, pinnedListing} = props.results;
    const listings = mergeListingsCollectionWithPinnedListing(
        listingsCollection,
        pinnedListing
    );

    return (
        <Fragment>
            {listings && (
                <Helmet>
                    <style>
                        {listings
                            .slice(0, MAX_PRELOAD_HIT_THUMBNAIL)
                            .filter(
                                (listing) =>
                                    !!(listing.images && listing.images[0])
                            )
                            .map((listing, index) => {
                                // Flow ignores check above
                                // $FlowFixMe
                                const url = listing.images[0].c6_uri;
                                return url
                                    ? `.${hitThumbnailBackgroundImageClass(
                                          index
                                      )} {background-image: url("${url}");}`
                                    : '';
                            })
                            .join(' ')}
                    </style>
                </Helmet>
            )}
            <ConnectedHitCollection {...props} />
        </Fragment>
    );
};

export default HitCollectionWithHelmet;
